// US state (2-letter) -> IANA timezone. Split states are mapped to their
// majority zone (e.g. FL=Eastern, TX=Central).
const US_STATE_ZONE = {
  AL: 'America/Chicago', AK: 'America/Anchorage', AZ: 'America/Phoenix', AR: 'America/Chicago',
  CA: 'America/Los_Angeles', CO: 'America/Denver', CT: 'America/New_York', DE: 'America/New_York',
  DC: 'America/New_York', FL: 'America/New_York', GA: 'America/New_York', HI: 'Pacific/Honolulu',
  ID: 'America/Denver', IL: 'America/Chicago', IN: 'America/New_York', IA: 'America/Chicago',
  KS: 'America/Chicago', KY: 'America/New_York', LA: 'America/Chicago', ME: 'America/New_York',
  MD: 'America/New_York', MA: 'America/New_York', MI: 'America/New_York', MN: 'America/Chicago',
  MS: 'America/Chicago', MO: 'America/Chicago', MT: 'America/Denver', NE: 'America/Chicago',
  NV: 'America/Los_Angeles', NH: 'America/New_York', NJ: 'America/New_York', NM: 'America/Denver',
  NY: 'America/New_York', NC: 'America/New_York', ND: 'America/Chicago', OH: 'America/New_York',
  OK: 'America/Chicago', OR: 'America/Los_Angeles', PA: 'America/New_York', RI: 'America/New_York',
  SC: 'America/New_York', SD: 'America/Chicago', TN: 'America/Chicago', TX: 'America/Chicago',
  UT: 'America/Denver', VT: 'America/New_York', VA: 'America/New_York', WA: 'America/Los_Angeles',
  WV: 'America/New_York', WI: 'America/Chicago', WY: 'America/Denver',
};

// Country code -> IANA timezone, for single-zone countries (and US territories).
// Multi-zone countries (CA, AU, MX, RU, BR, ...) are intentionally omitted so
// they fall back to the raw UTC offset rather than guessing a zone.
const COUNTRY_ZONE = {
  PR: 'America/Puerto_Rico', VI: 'America/St_Thomas',
  GU: 'Pacific/Guam', MP: 'Pacific/Saipan', AS: 'Pacific/Pago_Pago',
  GB: 'Europe/London', IE: 'Europe/Dublin', PT: 'Europe/Lisbon', ES: 'Europe/Madrid',
  FR: 'Europe/Paris', DE: 'Europe/Berlin', NL: 'Europe/Amsterdam', BE: 'Europe/Brussels',
  IT: 'Europe/Rome', CH: 'Europe/Zurich', AT: 'Europe/Vienna', SE: 'Europe/Stockholm',
  NO: 'Europe/Oslo', DK: 'Europe/Copenhagen', PL: 'Europe/Warsaw', CZ: 'Europe/Prague',
  HU: 'Europe/Budapest', RO: 'Europe/Bucharest', GR: 'Europe/Athens', FI: 'Europe/Helsinki',
  UA: 'Europe/Kyiv', TR: 'Europe/Istanbul',
  IN: 'Asia/Kolkata', PK: 'Asia/Karachi', BD: 'Asia/Dhaka', LK: 'Asia/Colombo',
  SG: 'Asia/Singapore', MY: 'Asia/Kuala_Lumpur', TH: 'Asia/Bangkok', VN: 'Asia/Ho_Chi_Minh',
  PH: 'Asia/Manila', HK: 'Asia/Hong_Kong', TW: 'Asia/Taipei', CN: 'Asia/Shanghai',
  JP: 'Asia/Tokyo', KR: 'Asia/Seoul', AE: 'Asia/Dubai', SA: 'Asia/Riyadh',
  IL: 'Asia/Jerusalem', QA: 'Asia/Qatar', KW: 'Asia/Kuwait',
  NG: 'Africa/Lagos', AO: 'Africa/Luanda', ZA: 'Africa/Johannesburg', EG: 'Africa/Cairo',
  KE: 'Africa/Nairobi', GH: 'Africa/Accra', MA: 'Africa/Casablanca', ET: 'Africa/Addis_Ababa',
  NZ: 'Pacific/Auckland',
};

// Friendly display name for the US zones (kept short and familiar). Other zones
// use the locale's own long name from Intl (e.g. "Central European Time").
const ZONE_NAME = {
  'America/New_York': 'Eastern Time',
  'America/Chicago': 'Central Time',
  'America/Denver': 'Mountain Time',
  'America/Phoenix': 'Mountain Time',
  'America/Los_Angeles': 'Pacific Time',
  'America/Anchorage': 'Alaska Time',
  'Pacific/Honolulu': 'Hawaii Time',
  'America/Puerto_Rico': 'Atlantic Time',
  'America/St_Thomas': 'Atlantic Time',
  'Pacific/Guam': 'Chamorro Time',
  'Pacific/Saipan': 'Chamorro Time',
  'Pacific/Pago_Pago': 'Samoa Time',
};

// Best timestamp we have for the visit, used so the abbreviation/offset
// reflect daylight saving as it was on the visit date.
function visitInstant(visitor) {
  const raw = visitor?.date_created || visitor?.visit_date || visitor?.action_date;
  if (raw) {
    const d = new Date(raw);
    if (!Number.isNaN(d.getTime())) return d;
  }
  return new Date();
}

// DST-aware abbreviation ("CDT"), long name ("Central Daylight Time") and
// offset ("UTC-05:00") for a zone on a given date.
function zoneParts(iana, date) {
  const part = (style) => {
    try {
      return new Intl.DateTimeFormat('en-US', { timeZone: iana, hour: 'numeric', timeZoneName: style })
        .formatToParts(date).find((p) => p.type === 'timeZoneName')?.value || '';
    } catch {
      return '';
    }
  };
  let abbr = part('short');
  // Intl gives a GMT-offset string ("GMT+1") for many non-US abbreviations,
  // which just duplicates the offset — keep only real letter codes (EST, IST).
  if (/^(GMT|UTC)/.test(abbr)) abbr = '';
  let offset = part('longOffset').replace('GMT', 'UTC');
  if (offset === 'UTC') offset = 'UTC+00:00';
  return { abbr, long: part('long'), offset };
}

// Format a UTC offset given in minutes (e.g. "-300" -> "UTC-05:00").
// Returns "-" when the value is missing or non-numeric.
export function formatOffset(raw) {
  if (raw === null || raw === undefined || raw === '') return '-';
  const minutes = parseInt(raw, 10);
  if (Number.isNaN(minutes)) return '-';
  const sign = minutes < 0 ? '-' : '+';
  const abs = Math.abs(minutes);
  const hh = String(Math.floor(abs / 60)).padStart(2, '0');
  const mm = String(abs % 60).padStart(2, '0');
  return `UTC${sign}${hh}:${mm}`;
}

// Human-friendly timezone label for a visitor/IP record, derived from the
// visitor's geographic location, e.g. "Central Time (CDT, UTC-05:00)" or
// "Central European Time (CET, UTC+01:00)". Falls back to the raw UTC offset
// (minutes) for multi-zone/unmapped countries, then "-".
export function formatTimezone(visitor) {
  const country = String(visitor?.country || '').toUpperCase();
  const state = String(visitor?.state || '').toUpperCase();
  const iana = (country === 'US' && US_STATE_ZONE[state]) || COUNTRY_ZONE[country];

  if (iana) {
    const { abbr, long, offset } = zoneParts(iana, visitInstant(visitor));
    const name = ZONE_NAME[iana] || long;
    const detail = [abbr, offset].filter(Boolean).join(', ');
    if (name && detail) return `${name} (${detail})`;
    if (name) return name;
    if (detail) return detail;
  }

  return formatOffset(visitor?.timezone_offset);
}
